import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch } from '../../app/hooks';
import { markRefreshNudgeRefreshing, setRefreshNudge } from '../../store/investor/action';
import { getRefreshNudge } from '../../store/investor/investorApiHelper';
import type { ModuleLastRefreshedAt } from '../../store/investor/taskTrackerResolution';

type RefreshNudgeResponse = { showNudge?: boolean; lastRefreshedAt?: number | null; moduleLastRefreshedAt?: ModuleLastRefreshedAt };

type Input = { orgId: string | undefined; triggerMain: string | null; isFetchingData: boolean };

export function useRefreshNudge({ orgId, triggerMain, isFetchingData }: Input) {
    const dispatch = useAppDispatch();
    const currentOrgIdRef = useRef(orgId);
    currentOrgIdRef.current = orgId;
    const isFetchingDataRef = useRef(isFetchingData);
    isFetchingDataRef.current = isFetchingData;
    const wasFetchingDataRef = useRef(isFetchingData);
    const requestSeqRef = useRef(0);
    const resetOrgIdRef = useRef<string | undefined | null>(null);
    // First request issued after a refresh finished; an older response must never read as post-refresh data.
    const edgeSeqRef = useRef(0);

    const fetchRefreshNudge = useCallback(
        (requestedOrgId: string) => {
            requestSeqRef.current += 1;
            const seq = requestSeqRef.current;
            const isCurrent = () => seq === requestSeqRef.current && currentOrgIdRef.current === requestedOrgId;
            getRefreshNudge(
                { orgId: requestedOrgId },
                {
                    onSuccess: (data: RefreshNudgeResponse) => {
                        if (!isCurrent()) return;
                        const isPostRefresh = !isFetchingDataRef.current && seq >= edgeSeqRef.current;
                        dispatch(
                            setRefreshNudge({
                                orgId: requestedOrgId,
                                status: isPostRefresh ? 'loaded' : 'refreshing',
                                showNudge: data.showNudge === true,
                                lastRefreshedAt: data.lastRefreshedAt ?? null,
                                moduleLastRefreshedAt: data.moduleLastRefreshedAt ?? {},
                            }),
                        );
                    },
                    onError: () => {
                        if (isCurrent()) dispatch(setRefreshNudge({ orgId: requestedOrgId, status: 'error' }));
                    },
                },
            );
        },
        [dispatch],
    );

    useEffect(() => {
        if (resetOrgIdRef.current !== orgId) {
            resetOrgIdRef.current = orgId;
            dispatch(setRefreshNudge(null));
        }
        if (orgId) fetchRefreshNudge(orgId);
    }, [orgId, triggerMain]);

    useEffect(() => {
        const wasFetchingData = wasFetchingDataRef.current;
        wasFetchingDataRef.current = isFetchingData;
        if (!orgId || wasFetchingData === isFetchingData) return;
        if (isFetchingData) {
            dispatch(markRefreshNudgeRefreshing(orgId));
            return;
        }
        edgeSeqRef.current = requestSeqRef.current + 1;
        fetchRefreshNudge(orgId);
    }, [isFetchingData]);
}
